import React, { useEffect } from 'react';
import * as Styles from './ChooseSiteOption.styles';
import WordpressAIImageDesktopBR from '@/media/onboarding/wordpress-ai-option-br.png';
import WordpressAIImageDesktopES from '@/media/onboarding/wordpress-ai-option-es.png';
import {
  Button,
  IconWordpress,
  IconPicture,
  IconCode,
  IconStarsOutline,
  IconCheck,
  IconButton,
} from 'gatorcomponents';
import { BlockOptions } from './BlockOptions';
import { isBRServer } from '@/utils/Validators/validation';
import { useResize } from '@/hooks/useResize';
import { BlockOptionsV2 } from './BlockOptionsV2';
import { GradientButton } from '@/pages/Studio/components';
import { ChooseSiteOptionBuilderV2 } from './ChooseSiteOptionBuilderV2';

const ChooseSiteOption = ({
  locales,
  buttonsProps,
  setCurrentFlow,
  goBack,
  enableSiteBuilderV2,
  highlightBuilderV2,
}) => {
  useEffect(() => {
    setCurrentFlow('');
  });

  const wordpressAIOption = {
    title: locales.wordpressAI.title,
    subtitle: locales.wordpressAI.subtitle,
    tag: locales.wordpressAI.tag,
    description: locales.wordpressAI.description,
    bulletPoints: locales.wordpressAI.bulletPoints,
    buttonLabel: locales.wordpressAI.buttonLabel,
    buttonExperimentalLabel: locales.wordpressAI.buttonExperimentalLabel,
    testId: 'wordpress-ai-block-button',
    buttonClick: () => buttonsProps.wordpressAI.onClick(),
  };

  const wordpressAIOptionExperimental = {
    ...locales.wordpressAIExperimental,
    icon: <IconWordpress
      color="highDark"
      size="md"
      testId="wordpress-icon"
    />,
    testId: 'wordpress-ai-experimental-block-button',
    buttonClick: () => buttonsProps.wordpressAIExperimental.onClick(),
    buttonAuxClick: () => buttonsProps.wordpressAIExperimental.onAuxClick(),
  };

  const wordpressOption = {
    icon: <IconWordpress
      color="highDark"
      size="md"
      testId="wordpress-icon"
    />,
    title: locales.wordpress.title,
    subtitle: locales.wordpress.subtitle,
    description: locales.wordpress.description,
    buttonLabel: locales.wordpress.buttonLabel,
    buttonExperimentalLabel: locales.wordpress.buttonExperimentalLabel,
    buttonClick: () => buttonsProps.wordpress.onClick(),
    buttonTestId: 'wordpress-normal-block-button',
  };

  const siteBuilderOption = {
    icon: <IconPicture
      color="highDark"
      size="md"
      testId="sitebuilder-icon"
    />,
    title: locales.siteBuilder.title,
    subtitle: locales.siteBuilder.subtitle,
    description: locales.siteBuilder.description,
    buttonLabel: locales.siteBuilder.buttonLabel,
    buttonExperimentalLabel: locales.siteBuilder.buttonExperimentalLabel,
    buttonClick: () => buttonsProps.siteBuilder.onClick(),
    buttonTestId: 'sitebuilder-block-button',
  };

  const otherToolsOption = {
    icon: <IconCode
      color="highDark"
      size="md"
      testId="othertools-icon"
    />,
    title: locales.otherTools.title,
    subtitle: locales.otherTools.subtitle,
    description: locales.otherTools.description,
    buttonLabel: locales.otherTools.buttonLabel,
    buttonExperimentalLabel: locales.otherTools.buttonExperimentalLabel,
    buttonClick: () => buttonsProps.otherTools.onClick(),
    buttonTestId: 'othertools-block-button',
  };

  const otherToolsOptionExperimental = {
    ...locales.otherToolsExperimental,
    icon: <IconCode
      color="highDark"
      size="md"
      testId="othertools-icon"
    />,
    buttonClick: () => buttonsProps.otherTools.onClick(),
    buttonTestId: 'othertools-experimental-block-button',
  };

  const siteBuilderV2Option = {
    icon: <IconPicture
      color="highDark"
      size="md"
      testId="sitebuilder-v2-icon"
    />,
    title: locales.siteBuilderV2.title,
    subtitle: locales.siteBuilderV2.subtitle,
    description: locales.siteBuilderV2.description,
    buttonLabel: locales.siteBuilderV2.buttonLabel,
    buttonExperimentalLabel: locales.siteBuilderV2.buttonExperimentalLabel,
    buttonClick: () => buttonsProps.siteBuilderV2.onClick(),
    buttonTestId: 'sitebuilder-v2-block-button',
    badge: locales.siteBuilderV2.badge,
  };

  const windowRect = useResize();
  const { width } = windowRect;
  const isDesktop = width > 1280;
  const shouldHighlightBuilderV2 = enableSiteBuilderV2 && highlightBuilderV2;
  const shouldHighlightWordpressAI = enableSiteBuilderV2 && !highlightBuilderV2;

  return (
    <Styles.Wrapper data-testid="choose-site-option-step">
      <Styles.Greeting>
        {shouldHighlightBuilderV2 ? locales.greetingExperimental : locales.greeting}
      </Styles.Greeting>

      {shouldHighlightBuilderV2 && (
        <ChooseSiteOptionBuilderV2
          banner={{
            ...locales.sitebuilderV2Banner,
            button: {
              ...locales.sitebuilderV2Banner.button,
              onClick: buttonsProps.siteBuilderV2Banner.onClick,
            },
          }}
          blockOptions={[
            wordpressAIOptionExperimental,
            otherToolsOptionExperimental,
          ]}
        />
      )}

      {!shouldHighlightBuilderV2 && (
        <>
          <Styles.WordpressAIWrapper>
            <Styles.WordpressAIContainer>
              <Styles.WordpressAIAbout>
                <Styles.WordpressAIAboutContent>

                  <Styles.WordpressAIAboutHeader>
                    <Styles.WordpressAITag>
                      {wordpressAIOption.tag}
                    </Styles.WordpressAITag>
                    <Styles.WordpressAITitle>{wordpressAIOption.title}</Styles.WordpressAITitle>
                  </Styles.WordpressAIAboutHeader>

                  <Styles.WordpressAIDescription>{wordpressAIOption.description}</Styles.WordpressAIDescription>
                  <Styles.BulletPointsList>
                    {wordpressAIOption.bulletPoints.map(item => (
                      <Styles.BulletPointsItem key={item}>
                        <IconCheck color="activePure" />
                        {item}
                      </Styles.BulletPointsItem>
                    ))}
                  </Styles.BulletPointsList>
                  <Styles.WordpressAIButton>
                    {shouldHighlightWordpressAI || false ? (
                      <GradientButton
                        testId="wordpress-ai-experimental-button"
                        label={wordpressAIOption.buttonExperimentalLabel}
                        title={wordpressAIOption.buttonExperimentalLabel}
                        gradientVariant="lightBlueGradient"
                        iconRight={<IconStarsOutline size="md" color="highPure" />}
                        onClick={wordpressAIOption.buttonClick}
                      />
                    )
                      : (
                        <Button
                          iconLeft={null}
                          iconRight={<IconStarsOutline size="md" color="highPure" />}
                          label={wordpressAIOption.buttonLabel}
                          title={wordpressAIOption.buttonLabel}
                          size="large"
                          testId={wordpressAIOption.testId}
                          variant="primary"
                          width="fit-content"
                          onClick={wordpressAIOption.buttonClick}
                        />
                      )}
                  </Styles.WordpressAIButton>
                </Styles.WordpressAIAboutContent>
              </Styles.WordpressAIAbout>

              {isDesktop && <Styles.WordpressAIImage src={isBRServer ? WordpressAIImageDesktopBR : WordpressAIImageDesktopES} />}
            </Styles.WordpressAIContainer>
          </Styles.WordpressAIWrapper>

          <Styles.OthersOptionsWrapper>
            {/* Flag on, but experiment highlight Wordpress AI */}
            {shouldHighlightWordpressAI && (
              <Styles.OptionsV2Adapter>
                <BlockOptionsV2 {...siteBuilderV2Option} buttonLabel={siteBuilderOption.buttonExperimentalLabel} />
                <BlockOptionsV2 {...wordpressOption} buttonLabel={wordpressOption.buttonExperimentalLabel} />
                <BlockOptionsV2 {...otherToolsOption} buttonLabel={otherToolsOption.buttonExperimentalLabel} />
              </Styles.OptionsV2Adapter>
            )}
            {/* Flag off, fallback */}
            {!enableSiteBuilderV2 && (
              <>
                <BlockOptions {...siteBuilderOption} />
                <BlockOptions {...wordpressOption} />
                <BlockOptions {...otherToolsOption} />
              </>
            )}
          </Styles.OthersOptionsWrapper>
        </>
      )}

      {goBack && (
        <Styles.BackWrapper>
          <IconButton
            testId="site-builder-v2-button-back"
            icon="IconArrow"
            onClick={goBack}
          />
        </Styles.BackWrapper>
      )}
    </Styles.Wrapper>
  );
};

export default ChooseSiteOption;
